/**
 * Comprehensive Error Handling System
 * 
 * Provides unified error handling with:
 * - User-friendly error message translations
 * - Network error handling with retry options
 * - Validation error display
 * - API error normalization
 */

import { NotificationOptions } from '../components/feedback/NotificationSystem';

export interface ErrorDetails {
  code?: string;
  field?: string;
  message: string;
  originalError?: any;
  statusCode?: number;
  timestamp?: string;
  context?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

export interface NetworkError extends Error {
  statusCode?: number;
  isNetworkError: boolean;
  isRetryable: boolean;
  retryAfter?: number;
}

export interface RetryOptions {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
}

/**
 * Error message translations for common API errors
 */
export const ERROR_MESSAGES = {
  // Authentication errors
  'AUTH_INVALID_CREDENTIALS': 'Invalid username or password',
  'AUTH_TOKEN_EXPIRED': 'Your session has expired. Please log in again',
  'AUTH_UNAUTHORIZED': 'You are not authorized to access this section',
  'AUTH_FORBIDDEN': 'Access denied',
  'AUTH_SESSION_EXPIRED': 'Your session has expired',

  // Validation errors
  'VALIDATION_REQUIRED': 'This field is required',
  'VALIDATION_EMAIL_INVALID': 'Please enter a valid email address',
  'VALIDATION_PASSWORD_WEAK': 'Password must be at least 8 characters with letters, numbers, and symbols',
  'VALIDATION_PASSWORD_MISMATCH': 'Passwords do not match',
  'VALIDATION_PHONE_INVALID': 'Please enter a valid phone number',
  'VALIDATION_FILE_SIZE': 'File size exceeds the allowed limit',
  'VALIDATION_FILE_TYPE': 'File type is not supported',

  // Network errors
  'NETWORK_OFFLINE': 'No internet connection available',
  'NETWORK_TIMEOUT': 'Request timed out. Please try again',
  'NETWORK_SERVER_ERROR': 'Server error. Please try again later',
  'NETWORK_CONNECTION_FAILED': 'Failed to connect to server',

  // File upload errors
  'UPLOAD_FAILED': 'File upload failed',
  'UPLOAD_SIZE_EXCEEDED': 'File size exceeds 5MB limit',
  'UPLOAD_TYPE_NOT_SUPPORTED': 'Only image files (JPG, PNG, GIF, WebP) are supported',

  // Profile errors
  'PROFILE_UPDATE_FAILED': 'Profile update failed',
  'PROFILE_PHOTO_UPLOAD_FAILED': 'Profile photo upload failed',
  'PASSWORD_CHANGE_FAILED': 'Password change failed',
  'PASSWORD_CURRENT_INCORRECT': 'Current password is incorrect',

  // Generic errors
  'UNKNOWN_ERROR': 'An unexpected error occurred',
  'SERVER_UNAVAILABLE': 'Server is unavailable',
  'REQUEST_FAILED': 'Request failed',
  'DATA_LOAD_FAILED': 'Failed to load data',

  // Admin errors
  'ADMIN_ACCESS_DENIED': 'Admin access denied',
  'USER_MANAGEMENT_FAILED': 'User management operation failed',
  'ADMIN_OPERATION_FAILED': 'Admin operation failed'
} as const;

/**
 * HTTP status code to error message mapping
 */
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request',
  401: 'Please log in to your account',
  403: 'You are not authorized to perform this action',
  404: 'Page or resource not found',
  408: 'Request timed out',
  409: 'Data conflict occurred',
  422: 'Invalid data provided',
  429: 'Too many requests. Please try again later',
  500: 'Server error. Please try again later',
  502: 'Server is unavailable',
  503: 'Service temporarily unavailable',
  504: 'Server connection timed out'
};

/**
 * Normalize different error types into a consistent format
 */
export function normalizeError(error: any, context?: string): ErrorDetails {
  const timestamp = new Date().toISOString();

  // Handle API errors with statusCode
  if (error?.statusCode) {
    return {
      code: `HTTP_${error.statusCode}`,
      message: error.message || HTTP_ERROR_MESSAGES[error.statusCode] || ERROR_MESSAGES.UNKNOWN_ERROR,
      statusCode: error.statusCode,
      originalError: error,
      timestamp,
      context
    };
  }

  // Handle validation errors
  if (error?.field && error?.message) {
    return {
      code: 'VALIDATION_ERROR',
      field: error.field,
      message: error.message,
      originalError: error,
      timestamp,
      context
    };
  }

  // Handle network errors
  if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
    return {
      code: 'NETWORK_ERROR',
      message: ERROR_MESSAGES.NETWORK_CONNECTION_FAILED,
      originalError: error,
      timestamp,
      context
    };
  }

  // Handle timeout errors
  if (error?.name === 'AbortError' || error?.message?.includes('timeout')) {
    return {
      code: 'NETWORK_TIMEOUT',
      message: ERROR_MESSAGES.NETWORK_TIMEOUT,
      originalError: error,
      timestamp,
      context
    };
  }

  // Handle generic errors
  return {
    code: 'UNKNOWN_ERROR',
    message: error?.message || ERROR_MESSAGES.UNKNOWN_ERROR,
    originalError: error,
    timestamp,
    context
  };
}

/**
 * Get user-friendly error message from error code
 */
export function getErrorMessage(code: string, fallback?: string): string {
  return ERROR_MESSAGES[code as keyof typeof ERROR_MESSAGES] || fallback || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/** apiService throws plain ApiError objects (not Error), so instanceof Error misses the server message. */
export function getThrownMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  if (error && typeof error === 'object') {
    const o = error as Record<string, unknown>;
    for (const key of ['message', 'Message', 'detail', 'Detail', 'title', 'Title'] as const) {
      const v = o[key];
      if (typeof v === 'string' && v.trim()) {
        return v;
      }
    }
  }
  return fallback;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Network errors are generally retryable
  if (error?.name === 'NetworkError' || error?.name === 'AbortError') {
    return true;
  }

  // Server errors (5xx) are retryable
  if (error?.statusCode >= 500) {
    return true;
  }

  // Rate limiting is retryable
  if (error?.statusCode === 429) {
    return true;
  }

  // Timeout errors are retryable
  if (error?.statusCode === 408) {
    return true;
  }

  return false;
}

/**
 * Create a network error with retry information
 */
export function createNetworkError(
  message: string,
  statusCode?: number,
  isRetryable: boolean = true,
  retryAfter?: number
): NetworkError {
  const error = new Error(message) as NetworkError;
  error.name = 'NetworkError';
  error.statusCode = statusCode;
  error.isNetworkError = true;
  error.isRetryable = isRetryable;
  error.retryAfter = retryAfter;
  return error;
}

/**
 * Extract validation errors from API response
 */
export function extractValidationErrors(error: any): ValidationError[] {
  const validationErrors: ValidationError[] = [];

  // Handle ASP.NET Core validation errors
  if (error?.errors && typeof error.errors === 'object') {
    Object.entries(error.errors).forEach(([field, messages]) => {
      if (Array.isArray(messages)) {
        messages.forEach((message: string) => {
          validationErrors.push({
            field: field.toLowerCase(),
            message,
            code: 'VALIDATION_ERROR'
          });
        });
      }
    });
  }

  // Handle single validation error
  if (error?.field && error?.message) {
    validationErrors.push({
      field: error.field,
      message: error.message,
      code: error.code || 'VALIDATION_ERROR'
    });
  }

  return validationErrors;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: ValidationError[]): Record<string, string[]> {
  const formatted: Record<string, string[]> = {};

  errors.forEach(error => {
    if (!formatted[error.field]) {
      formatted[error.field] = [];
    }
    formatted[error.field].push(error.message);
  });

  return formatted;
}

/**
 * Get notification options for different error types
 */
export function getErrorNotificationOptions(error: ErrorDetails): NotificationOptions {
  const baseOptions: NotificationOptions = {
    persistent: true,
    duration: 8000,
    context: error.context
  };

  // Network errors should have retry actions (keys resolved in useErrorHandler / NotificationSystem)
  if (error.code?.includes('NETWORK')) {
    return {
      ...baseOptions,
      title: 'connection.errorTitle',
      actions: [
        {
          label: 'retry.action',
          action: () => window.location.reload(),
          style: 'primary'
        }
      ]
    };
  }

  // Authentication errors should redirect to login
  if (error.code?.includes('AUTH')) {
    return {
      ...baseOptions,
      title: 'errors.authTitle',
      actions: [
        {
          label: 'errors.loginAgain',
          action: () => window.location.href = '/login',
          style: 'primary'
        }
      ]
    };
  }

  // Validation errors should be less persistent
  if (error.code?.includes('VALIDATION')) {
    return {
      ...baseOptions,
      persistent: false,
      duration: 6000,
      title: 'errors.validationTitle'
    };
  }

  return {
    ...baseOptions,
    title: 'error'
  };
}

/**
 * Retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2
  } = options;

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry if it's not a retryable error
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't wait after the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(backoffFactor, attempt), maxDelay);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Check if user is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Wait for network connection
 */
export function waitForConnection(timeout: number = 30000): Promise<boolean> {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      window.removeEventListener('online', onOnline);
      resolve(false);
    }, timeout);

    const onOnline = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('online', onOnline);
      resolve(true);
    };

    window.addEventListener('online', onOnline);
  });
}